import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  EntrepotTableRow,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';
import { LogicielCaisseReasortPrioriteService } from 'app/services/logiciel-caisse/logiciel-caisse-reasort-priorite.service';

type ReasortTab = 'cibles' | 'priorites';

@Component({
  selector: 'app-logiciel-caisse-reasort',
  templateUrl: './logiciel-caisse-reasort.component.html',
  styleUrls: ['./logiciel-caisse-reasort.component.scss'],
})
export class LogicielCaisseReasortComponent implements OnInit {
  tab: ReasortTab = 'cibles';
  searchTerm = '';
  loading = false;
  savingPriorite = false;

  /**
   * Magasins cibles (!réassort && !globale) :
   * - min/max stock
   * - priorité de réassort (ordre d’alimentation)
   * Les entrepôts is_reassort sont exclus : sources de stock uniquement.
   */
  cibles: EntrepotTableRow[] = [];
  filteredCibles: EntrepotTableRow[] = [];

  constructor(
    private readonly router: Router,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly prioriteService: LogicielCaisseReasortPrioriteService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadRows();
  }

  setTab(tab: ReasortTab): void {
    this.tab = tab;
    this.applySearch();
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredCibles = [...this.cibles];
      return;
    }
    this.filteredCibles = this.cibles.filter((row) => {
      const blob = `${row['CODE ENTREPÔT'] || ''} ${row.LIBELLÉ || ''}`.toLowerCase();
      return blob.includes(kw);
    });
  }

  openConfig(row: EntrepotTableRow): void {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) {
      this.toastr.error('Entrepôt invalide');
      return;
    }
    void this.router.navigate(['/main/parametre/reasort', id]);
  }

  moveUp(index: number): void {
    if (index <= 0 || this.searchTerm.trim()) {
      return;
    }
    const copy = [...this.cibles];
    [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]];
    this.cibles = copy;
    this.applySearch();
  }

  moveDown(index: number): void {
    if (index >= this.cibles.length - 1 || this.searchTerm.trim()) {
      return;
    }
    const copy = [...this.cibles];
    [copy[index], copy[index + 1]] = [copy[index + 1], copy[index]];
    this.cibles = copy;
    this.applySearch();
  }

  savePriorites(): void {
    if (this.searchTerm.trim()) {
      this.toastr.warning('Effacez la recherche avant d’enregistrer l’ordre.');
      return;
    }
    if (!this.cibles.length) {
      this.toastr.warning('Aucun magasin cible à prioriser.');
      return;
    }
    this.savingPriorite = true;
    this.prioriteService.putOrder(this.cibles.map((r) => String(r.id))).subscribe({
      next: () => {
        this.toastr.success('Priorités des magasins cibles enregistrées');
        this.savingPriorite = false;
        this.loadRows();
      },
      error: (err: Error) => {
        this.toastr.error(err.message || 'Échec enregistrement priorités');
        this.savingPriorite = false;
      },
    });
  }

  rowCode(row: EntrepotTableRow): string {
    return row['CODE ENTREPÔT'] || '—';
  }

  rowLibelle(row: EntrepotTableRow): string {
    return row.LIBELLÉ || '—';
  }

  private loadRows(): void {
    this.loading = true;
    this.entrepotService.getEntrepotsErpTableRows().subscribe({
      next: (items) => {
        const cibles = items.filter((r) => !r.is_reassort && !r.is_globale);
        this.cibles = this.sortByPriorite(cibles);
        this.applySearch();
        this.loading = false;
      },
      error: (err: Error) => {
        this.toastr.error(err.message || 'Impossible de charger les entrepôts');
        this.loading = false;
      },
    });
  }

  private sortByPriorite(rows: EntrepotTableRow[]): EntrepotTableRow[] {
    return [...rows].sort((a, b) => {
      const pa = this.prioNum(a);
      const pb = this.prioNum(b);
      if (pa !== pb) {
        return pa - pb;
      }
      return Number(a.id) - Number(b.id);
    });
  }

  private prioNum(row: EntrepotTableRow): number {
    const raw = row['PRIORITÉ'];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 999999;
  }
}
